import React, { useEffect, useRef } from 'react';
import { createStyles, Drawer, Theme } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import List from '@material-ui/core/List';
import { Omit } from '@material-ui/types';
import { DrawerProps } from '@material-ui/core/Drawer';
import { useSelector } from 'react-redux';
import PerfectScrollbar from 'perfect-scrollbar';
import { usePermission } from 'utils/permission';
import { getMenuItems, MenuItemModel } from 'store/slices/menuSlice';
import { useLoadBadge } from '../../../hooks/useLoadBadge';
import Logo from '../../atoms/Logo/Logo';
import AppMenuItem from './AppMenuItem';
import CustomIcon from './CustomIcon';
import { getUserInfo } from 'store/slices/userInfoSlice';
import { RouteNames } from '../../../router/route-list';

export const drawerWidth = 240;

const styles = (theme: Theme) =>
    createStyles({
        sidebarWrapper: {
            position: 'relative',
            height: 'calc(100vh - 56px)',
            overflow: 'auto',
            'z-index': '4',
            overflowScrolling: 'touch',
            transitionProperty: 'top, bottom, width',
            transitionDuration: '.2s, .2s, .35s',
            transitionTimingFunction: 'linear, linear, ease',
            color: 'inherit',
            paddingBottom: '30px',
        },
        sidebarWrapperWithPerfectScrollbar: {
            overflow: 'hidden !important',
        },
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
        },
        drawerPaper: {
            width: drawerWidth,
            background: theme.palette.primary.dark,
            color: '#fff',
        },
        toolbar: theme.mixins.toolbar,
        menuItem: {
            color: theme.palette.text.primary,
            width: drawerWidth,
        },
        category: {
            fontWeight: 'bolder',
            color: theme.palette.text.secondary,
        },
        appMenu: {
            width: '100%',
            overflow: 'hidden',
        },
        navList: {
            width: drawerWidth,
        },
        logo: {
            height: '50px',
            marginRight: '20px',
            flexShrink: 0,
        },
    });

const useStyles = makeStyles(styles);

type IProps = Omit<DrawerProps, 'classes'>;

const CommonSidebar = (props: IProps) => {
    const { branch } = useSelector(getUserInfo);
    const allMenu = useSelector(getMenuItems);
    const scrollbarRef = useRef<any>();
    const classes = useStyles();
    const checkPermission = usePermission();
    const categories = allMenu
        .filter((a) => !a.permissions || checkPermission(a.permissions!!))
        .filter(el => !(el.name === RouteNames.RELOG && !branch?.relog_enabled));
    const menuItems: MenuItemModel[] = [];

    for (const c of categories) {
        const temp = { ...c };
        temp.items = temp.items?.filter((a) => !a.permissions || checkPermission(a.permissions!!));
        menuItems.push(temp);
    }

    const { loadBadges } = useLoadBadge();

    useEffect(() => {
        if (scrollbarRef.current) {
            new PerfectScrollbar(scrollbarRef.current, {});
        }
    }, []);

    useEffect(() => {
        loadBadges();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Drawer
            className={classes.drawer}
            variant="permanent"
            {...props}
            classes={{
                paper: classes.drawerPaper,
            }}
        >
            <Logo className={classes.logo} />
            <List ref={scrollbarRef} component="div" className={classes.appMenu} disablePadding>
                {menuItems.map((category, index) => (
                    <AppMenuItem
                        {...category}
                        Icon={() => <CustomIcon icon={category.icon} />}
                        key={index}
                    />
                ))}
            </List>
        </Drawer>
    );
};

export default CommonSidebar;
